import React from "react";
import CustomModal from "../../../ui/CustomModal/CustomModal";
import CustomBadge from "../../../ui/CustomBadge/CustomBadge";
import { TbArrowsSplit } from "react-icons/tb";
import {
  TRANSACTION_STATUS_MAP,
  SERVICE_CODE_MAP,
} from "../../constants/transactionConstants";
import {
  extractRecipientName,
  getPaymentMethod,
} from "../../utils/transactionFormatters";
import {
  formatDateTimeToLocale,
  formatKES,
  getDisplayValue,
} from "../../../../utils/transactionutils";

const TransactionDetailsModal = ({
  opened,
  onClose,
  transactionData = {},
  canSplitTransaction = false,
  onSplitClick,
}) => {
  const {
    status,
    transactionDate,
    gatewayReference,
    recipientPartyName,
    accountNumber,
    description,
    amount,
    walletAmount,
    transactionCharges,
    taxes,
    walletRunningBalance,
    serviceCode,
    payoutAccountType,
    merchantTransactionReference,
    corePostingReference,
    coreTransactionSplit,
    coreTransactionSplitChannels,
  } = transactionData;

  const isInflow = SERVICE_CODE_MAP.inflow.includes(serviceCode);
  const backendStatus = status;
  const mappedStatus = TRANSACTION_STATUS_MAP[backendStatus] || "default";

  const isSuccessful = backendStatus === "SUCCESS";
  const isAlreadySplit = coreTransactionSplit === true;

  const splitChannels = coreTransactionSplitChannels || [];
  const totalSplitAmount = splitChannels.reduce(
    (sum, c) => sum + (Number(c.amount) || 0),
    0,
  );
  const bankSplitAmount = splitChannels
    .filter((c) => c.channel === "Bank")
    .reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
  const walletSplitAmount = (Number(walletAmount) || 0) - totalSplitAmount;
  const isSplitDisabled = !isInflow || !isSuccessful || isAlreadySplit;
  const splitTooltip = isAlreadySplit
    ? "Transaction already split"
    : !isInflow
      ? "Cannot split: Not an inflow transaction"
      : !isSuccessful
        ? "Cannot split: Transaction is not successful"
        : "Split Transaction";

  const rows = [
    {
      label: isInflow ? "Paid By" : "Sent To",
      value: extractRecipientName(recipientPartyName),
    },
    { label: "Account Number", value: getDisplayValue(accountNumber) },
    {
      label: "Description",
      value: getDisplayValue(description),
      fullText: true,
    },
    { label: "Amount", value: formatKES(amount) },
    { label: "Transfer Fee", value: formatKES(transactionCharges) },
    { label: "Taxes", value: formatKES(taxes) },
    {
      label: isInflow ? "Credited to Wallet" : "Debited from Wallet",
      value: formatKES(walletAmount),
    },
    { label: "Wallet Balance After", value: formatKES(walletRunningBalance) },
    {
      label: "Payment Method",
      value: getPaymentMethod(serviceCode, payoutAccountType, gatewayReference),
    },
    {
      label: "Merchant Reference",
      value: getDisplayValue(merchantTransactionReference),
    },
    { label: "Core Reference", value: getDisplayValue(corePostingReference) },
  ];

  return (
    <CustomModal
      opened={opened}
      onClose={onClose}
      centered
      overlayProps={{ backgroundOpacity: 0.4, blur: 4 }}
      radius="md"
      size="lg"
      fullScreen={false}
      styles={{ body: { padding: 0 } }}
    >
      <div
        style={{
          maxHeight: "calc(90dvh - 160px)",
          overflowY: "auto",
          padding: "0 1rem 1rem",
        }}
      >
        <div className="w-full space-y-4 sm:space-y-6">
          <div className="text-center border-b pb-3 sm:pb-4">
            <h1 className="text-base sm:text-xl font-semibold text-blue-700">
              Transaction Details
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-1">
              {formatDateTimeToLocale(transactionDate)}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 bg-gray-50 p-3 sm:p-4 rounded-md">
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 min-w-0">
              <span className="text-xs text-gray-500 uppercase tracking-wide shrink-0">
                Gateway Reference:
              </span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                {getDisplayValue(gatewayReference)}
              </span>
            </div>

            <CustomBadge color={mappedStatus} size="sm">
              {backendStatus || "UNKNOWN"}
            </CustomBadge>
          </div>

          {isAlreadySplit && (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-green-50 rounded-md p-3 sm:p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                  Wallet Amount
                </p>
                <p className="text-sm sm:text-base font-bold text-gray-900">
                  {formatKES(walletSplitAmount)}
                </p>
              </div>
              <div className="bg-blue-50 rounded-md p-3 sm:p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                  Bank Amount
                </p>
                <p className="text-sm sm:text-base font-bold text-gray-900">
                  {formatKES(bankSplitAmount)}
                </p>
              </div>
            </div>
          )}

          {canSplitTransaction && isInflow && !isAlreadySplit && (
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 bg-gray-50 p-3 sm:p-4 rounded-md">
              <span className="text-xs text-gray-500 uppercase tracking-wide shrink-0">
                Split Transaction:
              </span>
              <button
                type="button"
                title={splitTooltip}
                className={
                  isSplitDisabled
                    ? "flex items-center gap-1 rounded-full px-3 py-1 bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "flex items-center gap-1 rounded-full px-3 py-1 bg-primary-600 text-white hover:bg-primary-700"
                }
                onClick={() => {
                  if (!isSplitDisabled) {
                    onSplitClick?.(transactionData);
                  }
                }}
                disabled={isSplitDisabled}
              >
                <span className="text-xs">Split</span>
                <TbArrowsSplit size={16} />
              </button>
            </div>
          )}

          <dl className="space-y-2.5 sm:space-y-3">
            {rows.map(({ label, value, fullText }) => (
              <div
                key={label}
                className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2"
              >
                <dt className="text-xs sm:text-sm text-gray-500 font-semibold sm:w-44 shrink-0">
                  {label}:
                </dt>
                <dd
                  className={
                    fullText
                      ? "text-sm sm:text-base text-gray-900 whitespace-normal break-words"
                      : "text-sm sm:text-base text-gray-900 truncate"
                  }
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </CustomModal>
  );
};

export default TransactionDetailsModal;
